import React, { useState, useEffect } from "react";
import { productService, categoryService } from "../../services/api";
import { Product, Category } from "../../types";
import { useCart } from "../../context/CartContext";
import ProductCard from "../../components/ProductCard";
import LoadingSpinner from "../../components/LoadingSpinner";

const API_URL = process.env.REACT_APP_API_URL || "";

const Products: React.FC = () => {
    const { addToCart } = useCart();
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<number | null>(
        null,
    );
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [orderSuccess, setOrderSuccess] = useState("");
    const [orderModal, setOrderModal] = useState<{
        product: Product | null;
        selectedPriceId: number | null;
    }>({ product: null, selectedPriceId: null });

    const handleAddToCart = (product: Product) => {
        const firstActive = product.prices?.find((p) => p.is_active) ?? null;
        setOrderModal({ product, selectedPriceId: firstActive?.id ?? null });
    };

    const handleConfirmAdd = () => {
        if (!orderModal.product) return;
        const product = orderModal.product;
        const activePrices = product.prices?.filter((p) => p.is_active) ?? [];
        const selectedPrice =
            activePrices.find((p) => p.id === orderModal.selectedPriceId) ??
            activePrices[0] ??
            null;
        addToCart(product, selectedPrice);
        setOrderModal({ product: null, selectedPriceId: null });
        setOrderSuccess(`"${product.name}" ajouté au panier !`);
        setTimeout(() => setOrderSuccess(""), 3000);
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setIsLoading(true);
            const [productsData, categoriesData] = await Promise.all([
                productService.getAll(),
                categoryService.getAll(),
            ]);
            setProducts(productsData);
            setCategories(categoriesData);
        } catch (err) {
            setError("Erreur lors du chargement des produits");
        } finally {
            setIsLoading(false);
        }
    };

    const filteredProducts = selectedCategory
        ? products.filter((p) => p.category_id === selectedCategory)
        : products;

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="lg" text="Chargement des produits..." />
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 px-4 overflow-hidden relative">
            {/* Background image */}
            <div className="fixed inset-0 overflow-hidden">
                <div
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%) rotate(90deg)",
                        width: "100vh",
                        height: "100vw",
                        backgroundImage: "url('/bg.png')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                ></div>
            </div>
            {/* Background overlay with opacity */}
            <div className="fixed inset-0 bg-primary-dark/85"></div>

            {/* Animated background elements */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div
                    className="absolute top-0 left-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl animate-pulse"
                    style={{ animationDuration: "8s" }}
                />
                <div
                    className="absolute bottom-1/4 right-0 w-80 h-80 bg-accent/5 rounded-full blur-3xl animate-pulse"
                    style={{ animationDuration: "10s", animationDelay: "2s" }}
                />
                <div
                    className="absolute top-1/2 left-0 w-64 h-64 bg-accent/3 rounded-full blur-3xl animate-pulse"
                    style={{ animationDuration: "12s", animationDelay: "4s" }}
                />

                {/* Floating particles */}
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-1 h-1 bg-accent/30 rounded-full animate-[float_15s_ease-in-out_infinite]"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 10}s`,
                            animationDuration: `${15 + Math.random() * 10}s`,
                        }}
                    />
                ))}
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Hero Header */}
                <div className="mb-12 text-center animate-[fadeInDown_0.8s_ease-out]">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                        Nos{" "}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light animate-[shimmerText_3s_ease-in-out_infinite]">
                            Produits
                        </span>
                    </h1>

                    {/* Store info banner */}
                    <div className="inline-flex items-center space-x-4 bg-gradient-to-r from-accent/10 via-accent/5 to-accent/10 border border-accent/20 rounded-2xl px-6 py-4 animate-[fadeIn_1s_ease-out_0.5s_both]">
                        <div className="flex items-center space-x-2">
                            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                                <svg
                                    className="w-5 h-5 text-green-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                            </div>
                            <div className="text-left">
                                <p className="text-white text-sm font-medium">
                                    Ouvert maintenant
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {orderSuccess && (
                    <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400">
                        {orderSuccess}
                    </div>
                )}

                {error && (
                    <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 animate-[shake_0.5s_ease-in-out]">
                        {error}
                    </div>
                )}

                {selectedCategory === null ? (
                    <>
                        {/* Category Cards */}
                        <div className="mb-10 animate-[fadeIn_0.8s_ease-out_0.3s_both]">
                            <div className="flex items-center justify-center mb-6">
                                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                                <span className="px-4 text-gray-400 text-sm">
                                    Choisir une categorie
                                </span>
                                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
                                {categories.map((category, index) => {
                                    const categoryProductCount =
                                        products.filter(
                                            (p) =>
                                                p.category_id === category.id,
                                        ).length;
                                    const imageUrl = "/logo.png";

                                    return (
                                        <button
                                            key={category.id}
                                            onClick={() =>
                                                setSelectedCategory(category.id)
                                            }
                                            className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-accent/40 transition-all duration-300 hover:scale-[1.03] hover:shadow-xl hover:shadow-accent/10 animate-[fadeInUp_0.6s_ease-out_both] aspect-[4/3]"
                                            style={{
                                                animationDelay: `${0.2 + index * 0.1}s`,
                                            }}
                                        >
                                            {/* Category Image */}
                                            <img
                                                src={imageUrl}
                                                alt={category.name}
                                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                            />
                                            {/* Dark overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 group-hover:from-black/70 group-hover:via-black/30 transition-all duration-300" />

                                            {/* Content */}
                                            <div className="absolute inset-0 flex flex-col items-center justify-end p-4">
                                                <h3 className="text-white font-bold text-lg md:text-xl text-center drop-shadow-lg">
                                                    {category.name}
                                                </h3>
                                                <p className="text-gray-300 text-xs mt-1">
                                                    {categoryProductCount}{" "}
                                                    produit
                                                    {categoryProductCount > 1
                                                        ? "s"
                                                        : ""}
                                                </p>
                                            </div>

                                            {/* Hover accent border glow */}
                                            <div className="absolute inset-0 rounded-2xl border-2 border-accent/0 group-hover:border-accent/50 transition-all duration-300 pointer-events-none" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {categories.length === 0 && (
                            <div className="text-center py-16 animate-[fadeIn_0.8s_ease-out]">
                                <div className="w-24 h-24 mx-auto mb-6 bg-primary-light/50 rounded-full flex items-center justify-center">
                                    <svg
                                        className="w-12 h-12 text-gray-600"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                                        />
                                    </svg>
                                </div>
                                <p className="text-gray-400 text-xl mb-2">
                                    Aucune categorie disponible
                                </p>
                                <p className="text-gray-500 text-sm">
                                    Revenez bientot pour decouvrir nos
                                    nouveautes
                                </p>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* Back button + Category title */}
                        <div className="mb-8 animate-[fadeIn_0.5s_ease-out]">
                            <button
                                onClick={() => setSelectedCategory(null)}
                                className="group flex items-center space-x-2 text-gray-400 hover:text-accent transition-colors duration-200 mb-4"
                            >
                                <svg
                                    className="w-5 h-5 transition-transform duration-200 group-hover:-translate-x-1"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 19l-7-7 7-7"
                                    />
                                </svg>
                                <span className="text-sm font-medium">
                                    Retour aux categories
                                </span>
                            </button>

                            <div className="flex items-center space-x-3">
                                <h2 className="text-2xl md:text-3xl font-bold text-white">
                                    {
                                        categories.find(
                                            (c) => c.id === selectedCategory,
                                        )?.name
                                    }
                                </h2>
                                <span className="bg-accent/20 text-accent text-sm font-medium px-3 py-1 rounded-full">
                                    {filteredProducts.length} produit
                                    {filteredProducts.length > 1 ? "s" : ""}
                                </span>
                            </div>
                        </div>

                        {/* Products Grid */}
                        {filteredProducts.length === 0 ? (
                            <div className="text-center py-16 animate-[fadeIn_0.8s_ease-out]">
                                <div className="w-24 h-24 mx-auto mb-6 bg-primary-light/50 rounded-full flex items-center justify-center">
                                    <svg
                                        className="w-12 h-12 text-gray-600"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                                        />
                                    </svg>
                                </div>
                                <p className="text-gray-400 text-xl mb-2">
                                    Aucun produit dans cette categorie
                                </p>
                                <p className="text-gray-500 text-sm">
                                    Revenez bientot pour decouvrir nos
                                    nouveautes
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-4 max-w-3xl mx-auto">
                                {filteredProducts.map((product, index) => (
                                    <div
                                        key={product.id}
                                        className="animate-[fadeInUp_0.6s_ease-out_both]"
                                        style={{
                                            animationDelay: `${0.1 + index * 0.1}s`,
                                        }}
                                    >
                                        <ProductCard
                                            product={product}
                                            compact
                                            onAddToCart={handleAddToCart}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Custom keyframes */}
            <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.3;
          }
          25% {
            transform: translateY(-20px) translateX(10px);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-40px) translateX(-10px);
            opacity: 0.3;
          }
          75% {
            transform: translateY(-20px) translateX(5px);
            opacity: 0.6;
          }
        }
        @keyframes shimmerText {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        @keyframes gradientMove {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
      `}</style>

            {/* Modal sélection quantité */}
            {orderModal.product && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                    <div
                        className="absolute inset-0 bg-black/70"
                        onClick={() =>
                            setOrderModal({
                                product: null,
                                selectedPriceId: null,
                            })
                        }
                    />
                    <div className="relative bg-primary-light border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl">
                        <h2 className="text-xl font-bold text-white mb-1">
                            Ajouter au panier
                        </h2>
                        <p className="text-accent font-medium mb-5">
                            {orderModal.product.name}
                        </p>

                        {orderModal.product.prices &&
                        orderModal.product.prices.filter((p) => p.is_active)
                            .length > 0 ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Choisir la quantité
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {orderModal.product.prices
                                        .filter((p) => p.is_active)
                                        .map((p) => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                onClick={() =>
                                                    setOrderModal((m) => ({
                                                        ...m,
                                                        selectedPriceId: p.id,
                                                    }))
                                                }
                                                className={`py-2.5 px-3 rounded-xl border text-sm font-medium transition-all ${
                                                    orderModal.selectedPriceId ===
                                                    p.id
                                                        ? "border-accent bg-accent/20 text-accent"
                                                        : "border-white/10 text-gray-300 hover:border-accent/40"
                                                }`}
                                            >
                                                <span className="block font-semibold">
                                                    {p.label ||
                                                        `${p.quantity}${p.price_unit}`}
                                                </span>
                                                <span className="block text-xs mt-0.5 opacity-75">
                                                    {p.price}€
                                                </span>
                                            </button>
                                        ))}
                                </div>
                            </div>
                        ) : (
                            <p className="text-gray-400 text-sm">
                                Prix : {orderModal.product.price}€
                            </p>
                        )}

                        <div className="flex space-x-3 mt-6">
                            <button
                                onClick={() =>
                                    setOrderModal({
                                        product: null,
                                        selectedPriceId: null,
                                    })
                                }
                                className="flex-1 py-2.5 rounded-xl border border-white/20 text-gray-300 hover:bg-white/5 transition"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleConfirmAdd}
                                className="flex-1 py-2.5 rounded-xl bg-accent hover:bg-accent-light text-primary-dark font-semibold transition"
                            >
                                Ajouter
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Products;
